import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { REPORT_PAGE_SIZES, formatReportTotalLabel } from '../utils/reportGridPaging';
import { ReportFullscreenButton, useReportFullscreen } from './ReportFullscreenFrame';
import { GridColumnLayoutBar, GridColumnTitle } from './GridColumnLayoutBar';
import { useGridColumnLayout } from './useGridColumnLayout';
import { columnPixelWidth } from '../utils/gridColumnLayout';
import './ReportBootstrapGrid.css';

function isColVisible(col) {
  return col && col.visible !== false;
}

function formatValue(value, format) {
  if (value == null || value === '') return '';
  if (!format) return String(value);

  if (typeof format === 'string') {
    if (format === 'currency' || format === 'try') {
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
    }
    if (format === 'boolean') {
      return value === true || value === 1 || value === '1' ? 'Evet' : 'Hayır';
    }
    if (format.startsWith('dd') || format.includes('yyyy')) {
      return formatDate(value, format);
    }
    return String(value);
  }

  if (typeof format === 'object') {
    if (format.type === 'currency') {
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      return n.toLocaleString('tr-TR', {
        style: 'currency',
        currency: format.currency || 'TRY',
      });
    }
    if (typeof format.formatter === 'function') {
      return format.formatter(value);
    }
  }

  return String(value);
}

function formatDate(value, pattern) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value ?? '');
  const pad = (n) => String(n).padStart(2, '0');
  const map = {
    dd: pad(d.getDate()),
    MM: pad(d.getMonth() + 1),
    yyyy: String(d.getFullYear()),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
  };
  return String(pattern).replace(/yyyy|MM|dd|HH|mm|ss/g, (tok) => map[tok] ?? tok);
}

function nextSort(prev, dataField) {
  if (!prev || prev[0]?.selector !== dataField) {
    return [{ selector: dataField, desc: false }];
  }
  if (!prev[0].desc) {
    return [{ selector: dataField, desc: true }];
  }
  return [];
}

function sortIndicator(sort, dataField) {
  const s = sort?.[0];
  if (!s || s.selector !== dataField) return '';
  return s.desc ? ' ▼' : ' ▲';
}

function normalizeText(value) {
  return String(value ?? '').toLocaleLowerCase('tr-TR');
}

function parseFilterExpression(input) {
  const match = String(input ?? '').trim().match(/^(>=|<=|>|<|=)?\s*(.*)$/);
  return { operator: match?.[1] || '', operand: match?.[2] || '' };
}

function parseNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const normalized = String(value ?? '').trim().replace(/\./g, '').replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function parseDateFilter(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  const text = String(value ?? '').trim();
  const tr = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{1,2}))?$/);
  const date = tr
    ? new Date(Number(tr[3]), Number(tr[2]) - 1, Number(tr[1]), Number(tr[4] || 0), Number(tr[5] || 0))
    : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function compareByOperator(actual, expected, operator) {
  if (operator === '>') return actual > expected;
  if (operator === '>=') return actual >= expected;
  if (operator === '<') return actual < expected;
  if (operator === '<=') return actual <= expected;
  return actual === expected;
}

function isDateColumn(col) {
  return col?.dataType === 'date'
    || (typeof col?.format === 'string' && (col.format.startsWith('dd') || col.format.includes('yyyy')));
}

function isNumberColumn(col) {
  return col?.dataType === 'number'
    || col?.format === 'currency'
    || col?.format === 'try'
    || col?.format?.type === 'currency';
}

export function reportGridRowMatches(row, columns, filters) {
  return columns.every((col) => {
    const filter = String(filters?.[col.dataField] ?? '').trim();
    if (!filter || !col.dataField || col.filterable === false) return true;
    const raw = typeof col.filterValueGetter === 'function'
      ? col.filterValueGetter(row)
      : row?.[col.dataField];
    const { operator, operand } = parseFilterExpression(filter);

    if (isNumberColumn(col)) {
      const actual = parseNumber(raw);
      const expected = parseNumber(operand);
      return actual != null && expected != null && compareByOperator(actual, expected, operator || '=');
    }
    if (isDateColumn(col) && operator) {
      const actual = parseDateFilter(raw);
      const expected = parseDateFilter(operand);
      return actual != null && expected != null && compareByOperator(actual, expected, operator);
    }

    const formatted = formatValue(raw, col.format);
    const haystack = normalizeText(formatted || raw);
    const needle = normalizeText(operand);
    return operator === '=' ? haystack === needle : haystack.includes(needle);
  });
}

export function sortReportGridRows(rows, columns, sort) {
  const descriptor = sort?.[0];
  if (!descriptor?.selector) return rows;
  const col = columns.find((item) => item.dataField === descriptor.selector);
  if (!col) return rows;
  const direction = descriptor.desc ? -1 : 1;
  return [...rows].sort((left, right) => {
    const getter = col.sortValueGetter;
    const a = typeof getter === 'function' ? getter(left) : left?.[descriptor.selector];
    const b = typeof getter === 'function' ? getter(right) : right?.[descriptor.selector];
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (isNumberColumn(col)) return ((parseNumber(a) ?? 0) - (parseNumber(b) ?? 0)) * direction;
    if (isDateColumn(col)) return ((parseDateFilter(a) ?? 0) - (parseDateFilter(b) ?? 0)) * direction;
    return String(a).localeCompare(String(b), 'tr', { numeric: true, sensitivity: 'base' }) * direction;
  });
}

/**
 * Reusable Bootstrap report table with server-side or in-memory operations.
 *
 * Modes:
 * 1) load-driven (preferred): pass `load({ skip, take, sort }) => Promise<{ data, totalCount }>`
 * 2) controlled: pass `data`, `totalCount`, `loading`, `pageIndex`, `pageSize`, `onPageChange`
 * 3) client operations: controlled data + `clientOperations` and optional `filterRow`
 *
 * Column: { dataField, caption, width?, format?, visible?, sortable?, cellRender?, align? }
 * cellRender(row, { value, formatted }) => ReactNode
 */
export default function ReportBootstrapGrid({
  columns = [],
  keyExpr = 'id',
  load = null,
  data: controlledData = null,
  totalCount: controlledTotal = null,
  loading: controlledLoading = null,
  pageIndex: controlledPageIndex = null,
  pageSize: controlledPageSize = null,
  sort: controlledSort = null,
  onPageChange = null,
  reloadKey = 0,
  autoLoad = true,
  pageSizeOptions = REPORT_PAGE_SIZES,
  defaultPageSize = 50,
  selectable = false,
  /** page: başlık kutusu yalnız görünen sayfayı seçer. all: filtreli listenin tamamını seçer. */
  selectionScope = 'page',
  selectedKeys = null,
  onSelectionChange = null,
  emptyText = 'Kayıt bulunamadı',
  loadingText = 'Yükleniyor…',
  className = '',
  tableClassName = 'table table-sm table-striped table-hover report-bs-grid__table',
  toolbar = null,
  maxHeight = null,
  onLoadingChange = null,
  clientOperations = false,
  filterRow = false,
  layoutKey = null,
  loadProgress = null,
  /** (row) => string | string[] | null — ekstra tbody tr class */
  getRowClassName = null,
  /** (row) => void — satıra tıklanınca */
  onRowClick = null,
  /** Rapor başlığının sağında, kolonların üstünde */
  resultActions = null,
}) {
  const isControlled = typeof load !== 'function';
  const usesExternalPaging = isControlled && !clientOperations;
  const [innerPageIndex, setInnerPageIndex] = useState(0);
  const [innerPageSize, setInnerPageSize] = useState(defaultPageSize);
  const [innerSort, setInnerSort] = useState([]);
  const [innerFilters, setInnerFilters] = useState({});
  const [innerData, setInnerData] = useState([]);
  const [innerTotal, setInnerTotal] = useState(0);
  const [innerLoading, setInnerLoading] = useState(
    () => !isControlled && autoLoad && typeof load === 'function'
  );
  const [innerSelected, setInnerSelected] = useState([]);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const loadSeq = useRef(0);

  const pageIndex = usesExternalPaging ? (controlledPageIndex ?? 0) : innerPageIndex;
  const pageSize = usesExternalPaging ? (controlledPageSize ?? defaultPageSize) : innerPageSize;
  const sort = usesExternalPaging ? (controlledSort ?? []) : innerSort;
  const sourceData = isControlled ? (controlledData ?? []) : innerData;
  const serverTotalCount = Number(isControlled ? (controlledTotal ?? 0) : innerTotal) || 0;
  const loading = isControlled ? Boolean(controlledLoading) : innerLoading;
  const selected = selectedKeys != null ? selectedKeys : innerSelected;

  useEffect(() => {
    if (typeof onLoadingChange === 'function') onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  const columnLayout = useGridColumnLayout(layoutKey, columns);
  const visibleColumns = useMemo(
    () => columnLayout.columns.filter(isColVisible),
    [columnLayout.columns],
  );
  const lockColumnWidths = columnLayout.canManage || columnLayout.layoutActive;
  const filteredData = useMemo(
    () => clientOperations
      ? sourceData.filter((row) => reportGridRowMatches(row, visibleColumns, innerFilters))
      : sourceData,
    [clientOperations, sourceData, visibleColumns, innerFilters]
  );
  const processedData = useMemo(
    () => clientOperations ? sortReportGridRows(filteredData, visibleColumns, sort) : filteredData,
    [clientOperations, filteredData, visibleColumns, sort]
  );
  const totalCount = clientOperations ? processedData.length : serverTotalCount;

  const pageCount = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)) || 1);
  const safePageIndex = Math.min(pageIndex, Math.max(0, pageCount - 1));
  const data = clientOperations
    ? processedData.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize)
    : sourceData;

  useEffect(() => {
    if (!loading || !loadProgress?.startedAt) return undefined;
    setClockNow(Date.now());
    const timer = window.setInterval(() => setClockNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [loading, loadProgress?.startedAt]);

  useEffect(() => {
    if (safePageIndex !== pageIndex && !usesExternalPaging) setInnerPageIndex(safePageIndex);
  }, [pageIndex, safePageIndex, usesExternalPaging]);

  const emitPageChange = useCallback((next) => {
    if (typeof onPageChange === 'function') {
      onPageChange(next);
    }
  }, [onPageChange]);

  const setSelection = useCallback((keys) => {
    if (selectedKeys == null) setInnerSelected(keys);
    if (typeof onSelectionChange === 'function') onSelectionChange(keys);
  }, [selectedKeys, onSelectionChange]);

  const runLoad = useCallback(async (opts) => {
    if (typeof load !== 'function') return;
    const seq = ++loadSeq.current;
    setInnerLoading(true);
    try {
      const result = await load({
        skip: opts.pageIndex * opts.pageSize,
        take: opts.pageSize,
        sort: opts.sort,
        pageIndex: opts.pageIndex,
        pageSize: opts.pageSize,
      });
      if (seq !== loadSeq.current) return;
      const rows = Array.isArray(result?.data) ? result.data : [];
      const tc = Number(result?.totalCount);
      setInnerData(rows);
      setInnerTotal(Number.isFinite(tc) && tc >= 0 ? tc : rows.length);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setInnerData([]);
      setInnerTotal(0);
      throw err;
    } finally {
      if (seq === loadSeq.current) setInnerLoading(false);
    }
  }, [load]);

  useEffect(() => {
    if (isControlled || !autoLoad) return;
    runLoad({ pageIndex: safePageIndex, pageSize, sort }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, autoLoad, reloadKey, safePageIndex, pageSize, sort, runLoad]);

  const changePage = (nextIndex) => {
    if (loading && !clientOperations) return;
    const idx = Math.max(0, Math.min(nextIndex, pageCount - 1));
    if (!usesExternalPaging) setInnerPageIndex(idx);
    emitPageChange({ pageIndex: idx, pageSize, sort });
  };

  const changePageSize = (nextSize) => {
    if (loading && !clientOperations) return;
    const size = Number(nextSize) || defaultPageSize;
    if (!usesExternalPaging) {
      setInnerPageSize(size);
      setInnerPageIndex(0);
    }
    emitPageChange({ pageIndex: 0, pageSize: size, sort });
  };

  const changeSort = (dataField) => {
    if (loading && !clientOperations) return;
    const next = nextSort(sort, dataField);
    if (!usesExternalPaging) {
      setInnerSort(next);
      setInnerPageIndex(0);
    }
    emitPageChange({ pageIndex: 0, pageSize, sort: next });
  };

  const rowKey = (row, idx) => {
    const k = row?.[keyExpr];
    return k != null ? k : idx;
  };

  const selectionUniverse = selectionScope === 'all' ? processedData : data;
  const allPageSelected = selectionUniverse.length > 0
    && selectionUniverse.every((r, i) => selected.includes(rowKey(r, i)));
  const somePageSelected = selectionUniverse.some((r, i) => selected.includes(rowKey(r, i)));

  const toggleAllPage = () => {
    const universe = selectionScope === 'all' ? processedData : data;
    if (allPageSelected) {
      const pageKeys = new Set(universe.map((r, i) => rowKey(r, i)));
      setSelection(selected.filter((k) => !pageKeys.has(k)));
      return;
    }
    const merged = new Set(selected);
    universe.forEach((r, i) => merged.add(rowKey(r, i)));
    setSelection([...merged]);
  };

  const toggleRow = (key) => {
    if (selected.includes(key)) {
      setSelection(selected.filter((k) => k !== key));
    } else {
      setSelection([...selected, key]);
    }
  };

  const [gridFullscreen, toggleGridFullscreen] = useReportFullscreen();
  const scrollStyle = maxHeight && !gridFullscreen ? { maxHeight } : undefined;
  const toolbarNode = typeof toolbar === 'function' ? toolbar({ loading }) : toolbar;
  const activeFilterCount = Object.values(innerFilters).filter((value) => String(value ?? '').trim()).length;
  const progressLoaded = Number(loadProgress?.loadedCount ?? sourceData.length) || 0;
  const progressTotal = Number(loadProgress?.totalCount);
  const hasProgressTotal = loadProgress?.totalCount != null && Number.isFinite(progressTotal);
  const progressEnd = loadProgress?.finishedAt || clockNow;
  const progressSeconds = loadProgress?.startedAt
    ? Math.max(0, progressEnd - loadProgress.startedAt) / 1000
    : 0;
  const progressText = loadProgress?.startedAt
    ? `${loading ? 'Yükleniyor' : 'Yüklendi'}: ${progressLoaded.toLocaleString('tr-TR')}`
      + `${hasProgressTotal ? ` / ${progressTotal.toLocaleString('tr-TR')}` : ''} kayıt · ${progressSeconds.toFixed(1)} sn`
    : loadingText;

  const pagerInfoText =
    `Sayfa ${totalCount === 0 ? 0 : safePageIndex + 1} / ${totalCount === 0 ? 0 : pageCount} (${totalCount.toLocaleString('tr-TR')} kayıt)`;

  const renderPager = (placement) => (
    <div className={`report-bs-grid__pager report-bs-grid__pager--${placement}`}>
      <div className="report-bs-grid__pager-info">
        <span className="report-bs-grid__total">{formatReportTotalLabel(totalCount)}</span>
        <span className="report-bs-grid__pager-sep" aria-hidden="true">·</span>
        <span>{pagerInfoText}</span>
        {loadProgress?.startedAt || loading ? (
          <span className="report-bs-grid__loading-badge" aria-live="polite">{progressText}</span>
        ) : null}
        {clientOperations && activeFilterCount > 0 ? (
          <span className="report-bs-grid__filter-summary">
            {sourceData.length.toLocaleString('tr-TR')} kayıttan {totalCount.toLocaleString('tr-TR')} eşleşme
          </span>
        ) : null}
      </div>
      <div className="report-bs-grid__pager-controls">
        <label>
          Sayfa boyutu{' '}
          <select
            value={pageSize}
            onChange={(e) => changePageSize(e.target.value)}
            disabled={loading && !clientOperations}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(loading && !clientOperations) || safePageIndex <= 0} onClick={() => changePage(0)}>
          «
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(loading && !clientOperations) || safePageIndex <= 0} onClick={() => changePage(safePageIndex - 1)}>
          ‹
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(loading && !clientOperations) || safePageIndex >= pageCount - 1 || totalCount === 0} onClick={() => changePage(safePageIndex + 1)}>
          ›
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(loading && !clientOperations) || safePageIndex >= pageCount - 1 || totalCount === 0} onClick={() => changePage(pageCount - 1)}>
          »
        </button>
      </div>
    </div>
  );

  return (
    <div className={`report-bs-grid ${loading ? 'is-loading' : ''} ${clientOperations ? 'is-client-operations' : ''} ${gridFullscreen ? 'is-fullscreen' : ''} ${className}`.trim()}>
      {loading ? <div className="report-bs-grid__progress" aria-hidden="true" /> : null}

      {toolbarNode ? (
        <div className="report-bs-grid__actions">
          <div className="report-bs-grid__section-label">İşlemler</div>
          <div className="report-bs-grid__actions-row">{toolbarNode}</div>
        </div>
      ) : null}

      <div className="report-bs-grid__paging">
        <div className="report-bs-grid__section-label">Toplam / Sayfalama</div>
        {renderPager('top')}
      </div>

      <div className="report-bs-grid__result">
        <div className="report-bs-grid__result-head">
          <div className="report-bs-grid__section-label">Rapor</div>
          <div className="report-bs-grid__result-actions">
            {resultActions}
            <GridColumnLayoutBar
              enabled={columnLayout.canManage}
              columns={columnLayout.columns}
              onMoveBy={columnLayout.moveBy}
              onToggle={columnLayout.setVisible}
              onWidth={columnLayout.resize}
              onSave={columnLayout.save}
              onReset={columnLayout.reset}
            />
            <ReportFullscreenButton pressed={gridFullscreen} onClick={toggleGridFullscreen} />
          </div>
        </div>
        <div className="report-bs-grid__scroll" style={scrollStyle}>
          {loading && !clientOperations && (
            <div className="report-bs-grid__loading" aria-live="polite">
              <div className="report-bs-grid__spinner" />
              <span>{progressText}</span>
            </div>
          )}
          <table className={`${tableClassName}${lockColumnWidths ? ' is-sized' : ''}`}>
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      disabled={loading}
                      ref={(el) => {
                        if (el) el.indeterminate = !allPageSelected && somePageSelected;
                      }}
                      onChange={toggleAllPage}
                      aria-label={selectionScope === 'all' ? 'Tüm listeyi seç' : 'Sayfadaki tümünü seç'}
                    />
                  </th>
                )}
                {visibleColumns.map((col) => {
                  const sortable = col.sortable !== false && col.dataField;
                  const px = columnPixelWidth(col) || (lockColumnWidths ? 120 : null);
                  return (
                    <th
                      key={col._layoutId || col.dataField || col.caption}
                      style={px ? { minWidth: px, width: px, maxWidth: px } : undefined}
                      className={sortable ? 'sortable' : undefined}
                      onClick={sortable ? () => changeSort(col.dataField) : undefined}
                      title={sortable ? 'Sıralamak için tıklayın' : undefined}
                    >
                      <GridColumnTitle
                        column={col}
                        extra={sortable ? sortIndicator(sort, col.dataField) : null}
                        canManage={columnLayout.canManage}
                        onResize={columnLayout.resize}
                        onDropColumn={columnLayout.move}
                      />
                    </th>
                  );
                })}
              </tr>
              {filterRow && clientOperations ? (
                <tr className="report-bs-grid__filter-row">
                  {selectable ? (
                    <th>
                      {activeFilterCount > 0 ? (
                        <button
                          type="button"
                          className="report-bs-grid__clear-filters"
                          onClick={() => {
                            setInnerFilters({});
                            setInnerPageIndex(0);
                          }}
                          title="Kolon filtrelerini temizle"
                          aria-label="Kolon filtrelerini temizle"
                        >
                          ×
                        </button>
                      ) : null}
                    </th>
                  ) : null}
                  {visibleColumns.map((col) => (
                    <th key={col.dataField || col.caption}>
                      {col.dataField && col.filterable !== false ? (
                        <input
                          type="text"
                          className="form-control form-control-sm report-bs-grid__filter-input"
                          value={innerFilters[col.dataField] || ''}
                          placeholder={isNumberColumn(col) || isDateColumn(col) ? '=, >, < veya ara' : 'Ara…'}
                          aria-label={`${col.caption || col.dataField} filtresi`}
                          onChange={(event) => {
                            const value = event.target.value;
                            setInnerFilters((prev) => ({ ...prev, [col.dataField]: value }));
                            setInnerPageIndex(0);
                          }}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ) : null}
            </thead>
            <tbody>
              {!loading && data.length === 0 && (
                <tr>
                  <td
                    colSpan={(selectable ? 1 : 0) + Math.max(1, visibleColumns.length)}
                    className="report-bs-grid__empty"
                  >
                    {emptyText}
                  </td>
                </tr>
              )}
              {data.map((row, idx) => {
                const key = rowKey(row, idx);
                const isSelected = selected.includes(key);
                const extraRowClass = typeof getRowClassName === 'function'
                  ? getRowClassName(row)
                  : null;
                const rowClassName = [
                  isSelected ? 'selected' : null,
                  ...(Array.isArray(extraRowClass)
                    ? extraRowClass
                    : extraRowClass
                      ? [extraRowClass]
                      : []),
                ].filter(Boolean).join(' ') || undefined;
                return (
                  <tr
                    key={key}
                    className={rowClassName}
                    onClick={typeof onRowClick === 'function' ? () => onRowClick(row) : undefined}
                    style={typeof onRowClick === 'function' ? { cursor: 'pointer' } : undefined}
                  >
                    {selectable && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={loading}
                          onChange={() => toggleRow(key)}
                          aria-label="Satır seç"
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => {
                      const value = row?.[col.dataField];
                      const formatted = formatValue(value, col.format);
                      let content;
                      if (typeof col.cellRender === 'function') {
                        content = col.cellRender(row, { value, formatted, dataField: col.dataField });
                      } else {
                        content = formatted;
                      }
                      const px = columnPixelWidth(col) || (lockColumnWidths ? 120 : null);
                      return (
                        <td
                          key={col._layoutId || col.dataField || col.caption}
                          style={{
                            ...(px ? { minWidth: px, width: px, maxWidth: px } : null),
                            ...(col.align ? { textAlign: col.align } : null),
                          }}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPager('bottom')}
      </div>
    </div>
  );
}

export { formatValue as formatReportBootstrapCell };
